import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, BookImage, RefreshCw, X, Search, Loader2, MapPin, ImageOff, Pencil, Plus, Save, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const htmlToPlain = (html) => (html || "")
  .replace(/<\s*br\s*\/?>/gi, "\n")
  .replace(/<\/p>/gi, "\n\n")
  .replace(/<li[^>]*>/gi, "\n• ")
  .replace(/<[^>]+>/g, "")
  .replace(/&nbsp;/g, " ")
  .replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">")
  .trim();
// Convierte texto plano a HTML sencillo: párrafos por doble salto de línea, listas por líneas "•" o "- ".
const plainToHtml = (txt) => {
  const t = (txt || "").replace(/\r\n/g, "\n").trim();
  if (!t) return "";
  const blocks = t.split(/\n\n+/);
  return blocks.map((block) => {
    const lines = block.split("\n").filter((l) => l.trim() !== "");
    const bulletLines = lines.filter((l) => /^\s*[•\-*]\s+/.test(l));
    if (bulletLines.length && bulletLines.length === lines.length) {
      const items = lines.map((l) => `<li>${l.replace(/^\s*[•\-*]\s+/, "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`).join("");
      return `<ul>${items}</ul>`;
    }
    const inner = lines.map((l) => l.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")).join("<br/>");
    return `<p>${inner}</p>`;
  }).join("");
};

// It.3 · Descripción genérica ligada al producto: dada la plantilla TP vinculada a un producto
// del catálogo, devuelve el snapshot (descripción + fotos) para autorrellenar la cotización.
export async function salesFromPlantilla(plantillaId) {
  const { data } = await api.get(`/plantillas/${plantillaId}`);
  return { plantilla_id: data.id, sales_description: data.description || "", sales_images: data.images || [] };
}

const _STOP = new Set(["hotel", "tour", "tours", "private", "group", "small", "with", "from",
  "the", "and", "for", "experience", "visit", "guided", "walk", "transfer", "train", "flight",
  "reservation", "check", "day", "tickets", "entrance"]);
// Coincidencia REAL por nombre: comparten al menos un token significativo (≥4 letras, no stopword
// ni ciudad). Evita que cualquier plantilla de la misma ciudad bloquee el botón de IA.
const nameMatches = (a, b, city) => {
  const cityTok = new Set((city || "").toLowerCase().split(/[^a-z0-9áéíóúñ]+/).filter(Boolean));
  const norm = (s) => (s || "").toLowerCase().split(/[^a-z0-9áéíóúñ]+/)
    .filter((w) => w.length >= 4 && !_STOP.has(w) && !cityTok.has(w));
  const A = new Set(norm(a));
  return norm(b).some((w) => A.has(w));
};

// Bloque "Descripción de venta" reutilizable (servicios y alojamientos del Cotizador).
// Asocia una plantilla TP (lo que ve el cliente): snapshot de descripción + fotos, con
// sugerencia por nombre, cambio, actualización manual desde la plantilla y quitar.
export function CotSalesDescription({ name, plantillaId, description, images, onChange, testid, category = "", city = "" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [term, setTerm] = useState(name || "");
  const [q, setQ] = useState(name || "");
  const [refreshing, setRefreshing] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQ(term.trim()), 250); return () => clearTimeout(t); }, [term]);

  // Sugerencia inline (top match por nombre + ciudad + categoría) si aún no hay descripción asociada.
  const suggestQ = useQuery({
    queryKey: ["plantilla-suggest", name, category, city],
    enabled: !plantillaId && !description && (name || "").trim().length >= 3,
    queryFn: async () => (await api.get("/plantillas", { params: { prefer_type: "TP", q: name, category, city, limit: 1 } })).data.items || [],
  });
  const suggestion = (suggestQ.data || [])[0];
  const realMatch = suggestion && nameMatches(name, suggestion.name, city);

  const searchQ = useQuery({
    queryKey: ["plantilla-pick", q, category, city],
    enabled: pickerOpen && q.length >= 2,
    queryFn: async () => (await api.get("/plantillas", { params: { prefer_type: "TP", q, category, city, limit: 12 } })).data.items || [],
  });

  const apply = (p) => {
    onChange({ plantilla_id: p.id, sales_description: p.description || "", sales_images: p.images || [] });
    setPickerOpen(false);
    toast.success("Descripción de venta asociada");
  };
  const clear = () => onChange({ plantilla_id: null, sales_description: "", sales_images: [] });

  // Editor inline: sobreescribe SOLO en esta cotización (no cambia la plantilla original).
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [editImgs, setEditImgs] = useState([]);
  const [newImg, setNewImg] = useState("");
  const openEditor = () => {
    setEditText(htmlToPlain(description || ""));
    setEditImgs([...(images || [])]);
    setNewImg("");
    setEditOpen(true);
  };
  const saveEditor = () => {
    onChange({ sales_description: plainToHtml(editText), sales_images: editImgs });
    setEditOpen(false);
    toast.success("Descripción actualizada (solo en esta cotización)");
  };
  const addEditImg = () => {
    const u = (newImg || "").trim();
    if (!u) return;
    setEditImgs((prev) => [...prev, u]);
    setNewImg("");
  };
  const removeEditImg = (i) => setEditImgs((prev) => prev.filter((_, idx) => idx !== i));
  const refresh = async () => {
    if (!plantillaId) return;
    setRefreshing(true);
    try {
      const { data } = await api.get(`/plantillas/${plantillaId}`);
      onChange({ sales_description: data.description || "", sales_images: data.images || [] });
      toast.success("Descripción actualizada desde la plantilla");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setRefreshing(false); }
  };
  const openPicker = () => { setTerm(name || ""); setPickerOpen(true); };

  // E2-IA: solo si NO hay plantilla asociada NI sugerencia por nombre (se prioriza reutilizar).
  const aiGenerate = async () => {
    if (!(name || "").trim()) { toast.error("Falta el nombre del servicio"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/plantillas/ai-suggest", { name, city, category });
      onChange({ plantilla_id: data.id, sales_description: data.description || "", sales_images: data.images || [] });
      toast.success("Descripción generada con IA (pendiente de revisión)");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setAiBusy(false); }
  };

  return (
    <div className="mt-2 rounded-md border border-dashed bg-muted/20 p-2" data-testid={testid}>
      {description ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <BookImage className="h-3.5 w-3.5 text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Descripción de venta</span>
            {images?.length > 0 && <Badge variant="outline" className="h-4 text-[9px]">{images.length} foto{images.length > 1 ? "s" : ""}</Badge>}
            <div className="ml-auto flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Editar texto e imágenes en esta cotización" onClick={openEditor} data-testid={`${testid}-edit`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Actualizar desde la plantilla" onClick={refresh} disabled={refreshing || !plantillaId} data-testid={`${testid}-refresh`}>{refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}</Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" title="Cambiar plantilla" onClick={openPicker} data-testid={`${testid}-change`}><Search className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" title="Quitar descripción" onClick={clear} data-testid={`${testid}-clear`}><X className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <div className="flex gap-2">
            {images?.[0] && <img src={images[0]} alt="" loading="lazy" className="h-12 w-16 shrink-0 rounded object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <p className="line-clamp-2 text-[11px] text-muted-foreground">{stripHtml(description)}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Descripción de venta</span>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={openPicker} data-testid={`${testid}-add`}><BookImage className="mr-1 h-3 w-3" />Asociar plantilla</Button>
          {suggestion && realMatch && (
            <button type="button" onClick={() => apply(suggestion)} className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary/20" data-testid={`${testid}-suggest`} title={suggestion.name}>
              <Sparkles className="h-3 w-3" /> Sugerencia: <span className="max-w-[180px] truncate font-medium">{suggestion.name}</span> · Usar
            </button>
          )}
          {!suggestQ.isFetching && !realMatch && (name || "").trim().length >= 3 && (
            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-primary hover:bg-primary/10" onClick={aiGenerate} disabled={aiBusy} data-testid={`${testid}-ai`} title="Generar una descripción de venta con IA (inglés). Solo disponible cuando no hay plantilla que reutilizar.">
              {aiBusy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1 h-3 w-3" />}
              {aiBusy ? "Generando…" : "Sugerir con IA"}
            </Button>
          )}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden" data-testid={`${testid}-picker`}>
          <DialogHeader>
            <DialogTitle>Asociar descripción de venta</DialogTitle>
            <DialogDescription>Reutiliza una descripción de la biblioteca de plantillas. La verá el cliente en su itinerario.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar plantilla por nombre, ciudad…" className="pl-8" autoFocus data-testid={`${testid}-picker-search`} />
          </div>
          <div className="max-h-[55vh] space-y-1.5 overflow-y-auto pr-1">
            {searchQ.isFetching && <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando…</div>}
            {!searchQ.isFetching && q.length >= 2 && (searchQ.data || []).length === 0 && <div className="px-1 py-3 text-center text-xs text-muted-foreground">Sin plantillas que coincidan.</div>}
            {q.length < 2 && <div className="px-1 py-3 text-center text-xs text-muted-foreground">Escribe al menos 2 caracteres.</div>}
            {(searchQ.data || []).map((p) => (
              <button key={p.id} type="button" onClick={() => apply(p)} className="flex w-full items-start gap-3 rounded-md border p-2 text-left transition-colors hover:bg-accent" data-testid={`${testid}-picker-opt-${p.id}`}>
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-muted">
                  {p.images?.[0] ? <img src={p.images[0]} alt="" loading="lazy" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageOff className="h-4 w-4" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Badge variant={p.type === "TP" ? "default" : "secondary"} className="h-4 text-[9px]">{p.type}</Badge>
                    {p.event_type}{p.location && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{p.location}</span>}
                  </p>
                  <p className="line-clamp-2 text-[11px] text-muted-foreground">{stripHtml(p.description)}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor inline de descripción + imágenes (solo para esta cotización) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden" data-testid={`${testid}-editor`}>
          <DialogHeader>
            <DialogTitle>Editar descripción de venta</DialogTitle>
            <DialogDescription>
              Estos cambios se guardan <strong>solo en esta cotización</strong>. La plantilla original no se modifica.
              Usa líneas que empiecen por <code>•</code> o <code>-</code> para listas con viñetas.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={10}
              placeholder="Descripción visible al cliente. Escribe párrafos, o líneas con • / - para bullets."
              className="font-mono text-[13px]"
              data-testid={`${testid}-editor-text`}
            />
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Imágenes ({editImgs.length})</span>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {editImgs.map((u, i) => (
                  <div key={i} className="group relative aspect-[4/3] overflow-hidden rounded border bg-muted" data-testid={`${testid}-editor-img-${i}`}>
                    <img src={u} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                    <button type="button" onClick={() => removeEditImg(i)} title="Quitar" className="absolute right-1 top-1 rounded bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`${testid}-editor-img-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <Input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="URL de la imagen (https://…)" className="h-8 text-sm" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEditImg(); } }} data-testid={`${testid}-editor-img-url`} />
                <Button size="sm" variant="outline" className="h-8" onClick={addEditImg} disabled={!newImg.trim()} data-testid={`${testid}-editor-img-add`}><Plus className="mr-1 h-3.5 w-3.5" />Añadir</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} data-testid={`${testid}-editor-cancel`}>Cancelar</Button>
            <Button onClick={saveEditor} data-testid={`${testid}-editor-save`}><Save className="mr-1.5 h-4 w-4" />Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
